import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Money,
  toast,
} from '@mlm/ui';
import { Decimal, resolveTopupBonuses } from '@mlm/shared';
import { api, getTenantFeatures } from '../lib/api';

/**
 * Pending wallet top-ups. Mirrors the manual-payments review flow: the
 * member-typed reference is hidden, the admin reads it from the proof image and
 * types it back (blind verify), then Approve credits the slot wallet (principal
 * + bracket bonus). `referenceProvided` = whether the member sent one;
 * `adminTypedReference` set = the verified chip.
 */
type PendingTopup = {
  id: string;
  slotId: string;
  slotCode: string;
  userId: string;
  amount: string;
  currency: string;
  method: string;
  referenceProvided: boolean;
  adminTypedReference: string | null;
  proofR2Key: string | null;
  scanStatus: 'pending' | 'clean' | 'infected';
  createdAt: string;
};

function scanVariant(status: PendingTopup['scanStatus']): 'success' | 'destructive' | 'muted' {
  if (status === 'clean') return 'success';
  if (status === 'infected') return 'destructive';
  return 'muted';
}

export function WalletTopupsPage() {
  const qc = useQueryClient();
  const [reviewing, setReviewing] = useState<PendingTopup | null>(null);
  const [proofErrorId, setProofErrorId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['topups-pending'],
    queryFn: async () => (await api.get<PendingTopup[]>('/admin/wallet/topups/pending')).data,
  });

  async function viewProof(id: string) {
    setProofErrorId(null);
    try {
      const res = await api.get<{ url: string }>(`/admin/wallet/topups/${id}/proof-url`);
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('proof-url failed', err);
      setProofErrorId(id);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending Wallet Top-Ups</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <p className="p-6 text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="p-6 text-sm text-destructive">Failed to load top-ups.</p>
        ) : !data?.length ? (
          <p className="p-6 text-sm text-muted-foreground">No pending top-ups.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b">
              <tr>
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-4 py-2 font-medium">Slot</th>
                <th className="px-4 py-2 font-medium">Method</th>
                <th className="px-4 py-2 font-medium text-right">Amount</th>
                <th className="px-4 py-2 font-medium">Reference</th>
                <th className="px-4 py-2 font-medium">Scan</th>
                <th className="px-4 py-2 font-medium">Proof</th>
                <th className="px-4 py-2 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.map((t) => (
                <tr key={t.id} className="border-b">
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {new Date(t.createdAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    <div className="font-mono">{t.slotCode}</div>
                    <Link to={`/members/${t.userId}`} className="text-primary hover:underline">
                      View member
                    </Link>
                  </td>
                  <td className="px-4 py-2">{t.method}</td>
                  <td className="px-4 py-2 text-right">
                    <Money amount={t.amount} currency={t.currency.toUpperCase()} direction="neutral" />
                  </td>
                  <td className="px-4 py-2">
                    {t.adminTypedReference ? (
                      <div className="flex items-center gap-1.5">
                        <Badge variant="success" className="text-[10px]">verified</Badge>
                        <span className="font-mono text-xs">{t.adminTypedReference}</span>
                      </div>
                    ) : t.referenceProvided ? (
                      <Badge variant="warning" className="text-[10px]">submitted</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <Badge variant={scanVariant(t.scanStatus)}>{t.scanStatus}</Badge>
                  </td>
                  <td className="px-4 py-2">
                    <Button size="sm" variant="outline" disabled={!t.proofR2Key} onClick={() => viewProof(t.id)}>
                      <ExternalLink className="h-3.5 w-3.5" /> View
                    </Button>
                    {proofErrorId === t.id ? (
                      <p className="mt-1 text-xs text-destructive">Could not load proof.</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" disabled={t.scanStatus === 'infected'} onClick={() => setReviewing(t)}>
                      Review…
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>

      <ReviewTopupDialog
        topup={reviewing}
        onClose={() => setReviewing(null)}
        onDone={() => {
          setReviewing(null);
          qc.invalidateQueries({ queryKey: ['topups-pending'] });
        }}
      />
    </Card>
  );
}

type VerifyResult = {
  match: boolean;
  adminTypedReference: string;
  memberTypedReference: string | null;
  alreadyVerified: boolean;
};

function ReviewTopupDialog({
  topup,
  onClose,
  onDone,
}: {
  topup: PendingTopup | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [adminRef, setAdminRef] = useState('');
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [reviewError, setReviewError] = useState<string | null>(null);

  // Preview the bonus the member will receive on approval. ALL matching brackets
  // apply (e.g. a "% to Wallet" + a "% to GC" bracket both credit), mirroring the
  // server's `resolveTopupBonuses`.
  const { data: features } = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const bonusPreview = (() => {
    if (!topup || !features) return [];
    try {
      return resolveTopupBonuses(features.wallet_topup_brackets ?? [], new Decimal(topup.amount));
    } catch {
      return [];
    }
  })();

  function reset() {
    setAdminRef('');
    setVerifyResult(null);
    setVerifyError(null);
    setRejectMode(false);
    setRejectReason('');
    setReviewError(null);
  }

  const verify = useMutation({
    mutationFn: async () => {
      if (!topup) throw new Error('no_topup');
      const res = await api.post<VerifyResult>(`/admin/wallet/topups/${topup.id}/verify-reference`, {
        adminTypedReference: adminRef.trim(),
      });
      return res.data;
    },
    onSuccess: (data) => {
      setVerifyResult(data);
      setVerifyError(null);
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: string; existingSlotCode?: string; message?: string } } }).response?.data;
      if (data?.error === 'duplicate_admin_reference') {
        setVerifyError(
          data.existingSlotCode
            ? `That reference was already used on slot ${data.existingSlotCode}.`
            : 'That reference was already used on another top-up.',
        );
      } else {
        setVerifyError(data?.message ?? 'Could not verify reference.');
      }
    },
  });

  const review = useMutation({
    mutationFn: async (decision: 'approved' | 'rejected') => {
      if (!topup) throw new Error('no_topup');
      const body: { decision: 'approved' | 'rejected'; rejectionReason?: string } = { decision };
      if (decision === 'rejected') body.rejectionReason = rejectReason.trim() || undefined;
      await api.post(`/admin/wallet/topups/${topup.id}/review`, body);
    },
    onSuccess: (_data, decision) => {
      toast.success(decision === 'approved' ? 'Top-up approved — wallet credited.' : 'Top-up rejected.');
      reset();
      onDone();
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: string; message?: string } } }).response?.data;
      setReviewError(data?.message ?? data?.error ?? 'Failed.');
    },
  });

  const verified = verifyResult !== null;
  const mismatch = verified && !verifyResult.match;

  return (
    <Dialog
      open={!!topup}
      onOpenChange={(o) => {
        if (!o && !verify.isPending && !review.isPending) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Review top-up {topup ? `· ${topup.slotCode}` : ''}</DialogTitle>
        </DialogHeader>

        {topup && (
          <div className="space-y-3">
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm border rounded-md p-3 bg-muted/30">
              <dt className="text-muted-foreground">Method</dt>
              <dd>{topup.method}</dd>
              <dt className="text-muted-foreground">Amount</dt>
              <dd><Money amount={topup.amount} currency={topup.currency.toUpperCase()} direction="neutral" /></dd>
              <dt className="text-muted-foreground">Bonus on approval</dt>
              <dd className="text-success">
                {bonusPreview.length > 0
                  ? bonusPreview
                      .map((b) => `+${b.bonus.toFixed(2)} ${b.bonusCurrency.toUpperCase()}`)
                      .join(' · ')
                  : '—'}
              </dd>
              <dt className="text-muted-foreground">Scan</dt>
              <dd>{topup.scanStatus}</dd>
            </dl>

            <p className="text-xs text-muted-foreground">
              View the proof picture, then type the reference number you read from it.{' '}
              {topup.referenceProvided
                ? 'The member submitted a reference; it will be revealed for comparison after you commit a value.'
                : 'The member did not submit a reference; only your typed value is saved.'}
            </p>

            {!verified ? (
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground" htmlFor="adminRef">
                  Reference from picture
                </label>
                <div className="flex gap-2">
                  <Input
                    id="adminRef"
                    value={adminRef}
                    onChange={(e) => setAdminRef(e.target.value)}
                    placeholder="e.g. 1234567890"
                    autoFocus
                    disabled={verify.isPending}
                  />
                  <Button onClick={() => verify.mutate()} disabled={!adminRef.trim() || verify.isPending}>
                    {verify.isPending ? 'Verifying…' : 'Verify'}
                  </Button>
                </div>
                {verifyError && <p className="text-sm text-destructive">{verifyError}</p>}
              </div>
            ) : (
              <div
                className={
                  'rounded-md border p-3 text-sm ' +
                  (mismatch
                    ? 'border-warning/40 bg-warning/10 text-foreground'
                    : 'border-success/40 bg-success/10 text-foreground')
                }
              >
                {mismatch ? (
                  <>
                    <p className="font-medium mb-1">⚠ Mismatch</p>
                    <p>
                      You typed <span className="font-mono">{verifyResult.adminTypedReference}</span>. The
                      member typed{' '}
                      <span className="font-mono">{verifyResult.memberTypedReference ?? '— (none)'}</span>.
                    </p>
                    <p className="text-xs mt-1 text-muted-foreground">Approving anyway will audit-log the mismatch.</p>
                  </>
                ) : (
                  <p>✓ Match: <span className="font-mono">{verifyResult.adminTypedReference}</span></p>
                )}
                <button
                  type="button"
                  className="mt-1 text-xs underline opacity-80 hover:opacity-100"
                  onClick={() => {
                    setAdminRef(verifyResult.adminTypedReference);
                    setVerifyResult(null);
                    setVerifyError(null);
                  }}
                >
                  Change reference
                </button>
              </div>
            )}

            {rejectMode && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Rejection reason (optional)</label>
                <Input
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="e.g. proof unreadable, amount doesn't match"
                  autoFocus
                />
              </div>
            )}

            {reviewError && <p className="text-sm text-destructive">{reviewError}</p>}
          </div>
        )}

        <DialogFooter>
          {!rejectMode ? (
            <>
              <Button variant="outline" onClick={() => { reset(); onClose(); }} disabled={verify.isPending || review.isPending}>
                Close
              </Button>
              <Button variant="destructive" onClick={() => setRejectMode(true)} disabled={verify.isPending || review.isPending}>
                Reject…
              </Button>
              <Button onClick={() => review.mutate('approved')} disabled={!verified || review.isPending}>
                {review.isPending && review.variables === 'approved'
                  ? 'Approving…'
                  : mismatch
                    ? 'Approve anyway'
                    : 'Approve'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setRejectMode(false)} disabled={review.isPending}>
                Back
              </Button>
              <Button variant="destructive" onClick={() => review.mutate('rejected')} disabled={review.isPending}>
                {review.isPending ? 'Rejecting…' : 'Confirm reject'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
